/**
 * @typedef {object} TableReloadSkeletonState
 * @property {boolean} hold - Keep table skeleton until the refetch cycle completes
 * @property {boolean} sawFetchWhileHolding - Saw `isTableFetching` while hold was active
 */

/** @type {TableReloadSkeletonState} */
export const initialTableReloadSkeletonState = {
  hold: false,
  sawFetchWhileHolding: false,
};

/**
 * Advances reload-skeleton hold state. Only user-initiated filter/pagination reload mutations
 * should drive `isReloadMutationPending`; background refetches (e.g. direct edit) must not.
 * @param {TableReloadSkeletonState} state
 * @param {object} inputs
 * @param {boolean} inputs.isReloadMutationPending
 * @param {boolean} inputs.isTableFetching
 * @returns {TableReloadSkeletonState}
 */
export function computeNextTableReloadSkeletonState(state, {
  isReloadMutationPending,
  isTableFetching,
}) {
  let { hold, sawFetchWhileHolding } = state;

  if (isReloadMutationPending) {
    hold = true;
    sawFetchWhileHolding = false;
  }

  if (hold && isTableFetching) {
    sawFetchWhileHolding = true;
  }

  if (
    hold
    && !isReloadMutationPending
    && !isTableFetching
    && sawFetchWhileHolding
  ) {
    hold = false;
    sawFetchWhileHolding = false;
  }

  return { hold, sawFetchWhileHolding };
}

/**
 * Whether the table body should show the loading skeleton.
 * @param {object} params
 * @param {boolean} params.isTableDataBlocking - Initial / blocking load
 * @param {boolean} params.isPlaceholderData - Page change with keepPreviousData
 * @param {boolean} params.holdForFilterReload - Hold until filter reload refetch ends
 * @returns {boolean}
 */
export function shouldShowTableDataSkeleton({
  isTableDataBlocking,
  isPlaceholderData,
  holdForFilterReload,
}) {
  return Boolean(isTableDataBlocking || isPlaceholderData || holdForFilterReload);
}
